import { useEffect, useMemo, useRef } from 'react';
import { z } from 'zod';

import { useCustomForm } from '@/shared/components/ui/form/context/form-context';
import { TUseCustomFieldsSectionProps } from '@/shared/features/custom-fields/hooks/use-custom-fields-section/use-custom-fields-section.types';
import { useReadCustomFieldsForAssetTypeQuery } from '@/shared/redux/api/assets/custom-fields/custom-fields-api';
import {
  CustomFieldConfigurationFragment,
  CustomFieldType,
} from '@/shared/types/types.generated';
import {
  optionalNumber,
  optionalString,
  requiredArray,
  requiredString,
} from '@/shared/utils/validation';

const getCustomFieldValueSchema = (field: CustomFieldConfigurationFragment) => {
  if (field.fieldType === CustomFieldType.Boolean) {
    return field.required ? z.boolean() : z.boolean().optional();
  }

  if (field.fieldType === CustomFieldType.MultiSelect) {
    return field.required
      ? requiredArray(z.string())
      : z.array(z.string()).optional();
  }

  if (field.fieldType === CustomFieldType.Number) {
    return field.required
      ? z.union([z.string().min(1), z.number()])
      : optionalNumber;
  }

  if (field.fieldType === CustomFieldType.Url) {
    return field.required
      ? requiredString({ url: true })
      : optionalString({ url: true });
  }

  return field.required ? requiredString() : optionalString();
};

const getCustomFieldsSchema = (fields: CustomFieldConfigurationFragment[]) => {
  const shape = fields.reduce<Record<string, z.ZodTypeAny>>((acc, field) => {
    acc[field.key] = getCustomFieldValueSchema(field);
    return acc;
  }, {});

  const schema = z.object(shape).passthrough();
  const hasRequiredFields = fields.some((field) => field.required);

  return hasRequiredFields ? schema : schema.optional();
};

export const useCustomFieldsSection = ({
  assetType,
  enabled,
}: TUseCustomFieldsSectionProps) => {
  const { schema, updateSchema } = useCustomForm();
  const appliedSchemaRef = useRef<z.ZodTypeAny | null>(null);
  const appliedFieldsSignatureRef = useRef<string | null>(null);

  const { data, isLoading } = useReadCustomFieldsForAssetTypeQuery(
    { assetType },
    { skip: !enabled },
  );

  const fields = useMemo(
    () => (enabled ? (data?.readCustomFieldsForAssetType ?? []) : []),
    [data?.readCustomFieldsForAssetType, enabled],
  );

  const fieldsSignature = fields
    .map((field) => `${field.fieldId}:${field.required}:${field.fieldType}`)
    .join('|');

  useEffect(() => {
    if (!enabled || !fields.length || !(schema instanceof z.ZodObject)) {
      return;
    }

    if (
      appliedSchemaRef.current === schema &&
      appliedFieldsSignatureRef.current === fieldsSignature
    ) {
      return;
    }

    const nextSchema = schema.extend({
      customFields: getCustomFieldsSchema(fields),
    });

    appliedSchemaRef.current = nextSchema;
    appliedFieldsSignatureRef.current = fieldsSignature;
    updateSchema(nextSchema);
  }, [enabled, fields, fieldsSignature, schema, updateSchema]);

  return {
    fields,
    isLoading,
  };
};
